/**
 * Install detection.
 *
 * iOS is the case that matters. Web push on iOS 16.4+ works ONLY from a
 * home-screen install -- never from a Safari tab, and there is no
 * beforeinstallprompt event to lean on. So iOS users must be walked
 * through Share -> Add to Home Screen explicitly, or notifications
 * silently never arrive and nobody knows why.
 */
export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // Safari's non-standard flag, still the only signal that works on iOS.
  (window.navigator as { standalone?: boolean }).standalone === true;

export const isIos = (): boolean =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS 13+ reports as desktop Safari but has a touch screen.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const canPush = (): boolean =>
  'serviceWorker' in navigator && 'PushManager' in window;

/**
 * True when the user is on iOS, in a browser tab, and therefore cannot
 * receive notifications until they install. This is the only state that
 * warrants interrupting them with a guide.
 */
export const needsIosInstall = (): boolean =>
  isIos() && !isStandalone();

export const registerServiceWorker = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) return;
  try {
    await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch {
    // A failed registration costs offline support, not function.
  }
};
